// @ts-check
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

// core
import {
  AnnouncementAlert,
  cloneCampaignAndNavigateToDetails,
  CloneCampaignFlowModal,
  DataTable,
  OPEN_MODE,
  resolveRouteWithId,
  routerUrl,
  useFeatureFlag,
} from 'core';
import { buildRecentCampaignTableConfig } from 'core/components/Tables/configs';

// state
import { useAppDispatch, useAppSelector } from 'core/store';
import {
  cloneCampaignAsync,
  closeAnnouncment,
  getActiveAnnouncementsAsync,
  getCampaignListAsync,
  resetAlertMessages,
  resetCampaign,
  resetCampaignPreview,
  resetPageSummary,
  selectMostRecentOpenAnnouncement,
  setCampaignData,
  setSelectedRoute,
} from 'core/store/slices';

// components
import { CalendarCard } from './components/CalendarCard';

const WelcomeContainer = styled.div`
  span.welcome-warning {
    background: #ffc627;
  }
`;

const ParagraphWithCardWrapper = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1.5fr; /* Left column wider than right column */
  gap: 130px;
  align-items: flex-start; /* Aligns CalendarCard with the first paragraph */
`;

const Home = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { campaigns, loading: campaignListLoading } = useAppSelector(
    (state) => state.campaignList,
  );
  const mostRecentOpenAnnouncement = useAppSelector(
    selectMostRecentOpenAnnouncement,
  );

  const isCloningEnabled = useFeatureFlag(
    'ACAN_CPBD_CLONING_INTEGRATION_387594',
  );
  const recentCampaignsTableConfig = useMemo(
    () =>
      buildRecentCampaignTableConfig({
        enableCloneColumn: isCloningEnabled,
      }),
    [isCloningEnabled],
  );

  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [campaignToClone, setCampaignToClone] = useState(null);

  useEffect(() => {
    dispatch(setSelectedRoute(routerUrl.HOME));
    dispatch(
      getCampaignListAsync({
        searchFilter: {
          perPage: 10,
          all: false,
        },
      }),
    );
    // @ts-ignore
    dispatch(getActiveAnnouncementsAsync());
  }, [dispatch]);

  useEffect(() => {
    setAnnouncementOpen(!!mostRecentOpenAnnouncement);
  }, [mostRecentOpenAnnouncement]);

  const closeCloneModal = () => {
    setCloneModalOpen(false);
    setCampaignToClone(null);
  };

  /**
   * Wrapper to keep callsites explicit and small.
   * @param {string} sourceCampaignId
   * @param {string} cloneName
   */
  const cloneToDetails = (sourceCampaignId, cloneName) =>
    cloneCampaignAndNavigateToDetails({
      dispatch,
      history,
      sourceCampaignId,
      cloneName,
      cloneCampaignAsync,
      resetAlertMessages,
      resetCampaignPreview,
      onClose: closeCloneModal,
      onAfterCloneSuccess: () => {
        dispatch(resetCampaign());
      },
    });

  /**
   * Clone the selected campaign using the provided name, then navigate to the
   * newly-created campaign Details page.
   *
   * Note:
   * Clears any existing banner alerts before starting.
   * Errors are handled by the clone thunk (banner error state); simply
   * short-circuit on failure.
   * Intentionally skip a list refresh here; the user is taken directly to
   * the cloned campaign.
   * @param {string} cloneName
   */
  const handleCloneAndRefresh = async (cloneName) => {
    const sourceCampaignId = campaignToClone?.campaign_id;
    if (!sourceCampaignId) return;

    await cloneToDetails(sourceCampaignId, cloneName);
  };

  const cloneSourceIncludesFile = Boolean(campaignToClone?.filename);

  return (
    <div>
      {announcementOpen && mostRecentOpenAnnouncement ? (
        <AnnouncementAlert
          content={mostRecentOpenAnnouncement.content}
          title={mostRecentOpenAnnouncement.title}
          isOpen={announcementOpen}
          onClose={() => {
            setAnnouncementOpen(false);
            dispatch(closeAnnouncment(mostRecentOpenAnnouncement));
          }}
        />
      ) : null}
      <WelcomeContainer>
        <h2 data-testid="home">Welcome to Campaign Builder</h2>
        <ParagraphWithCardWrapper>
          <div>
            <p>
              Campaign Builder has been designed to help you to create finely
              segmented lists of students for outreach. We collect metadata
              about the campaigns you run and why, which may be useful in
              evaluating the efficacy and impact of your work.
            </p>
            <p>
              <span className="highlight-gold welcome-warning">
                <strong>An important note:</strong>
              </span>{' '}
              Because Campaign Builder can create files that contain regulated
              information (for example, students with a FERPA hold), data
              generated by Campaign Builder should only be used for university
              business; Campaign Builder data should never be released to a
              third party.
            </p>
            <p>
              If you have any questions about the data or the application more
              broadly, please contact the Campaign Builder team at{' '}
              <a href="mailto:aacb@asu.edu">aacb@asu.edu</a> or via Slack
              #aacb_support.
            </p>
            <section className="mt-4 mb-4">
              <button
                type="button"
                className="btn btn-gold"
                data-ga-event="link"
                data-ga-action="click"
                data-ga-name="onclick"
                data-ga-type="cta"
                data-ga-region="main content"
                data-ga-section="Campaign Builder Home"
                data-ga-component=""
                data-ga-text="Create a new Campaign"
                onClick={() => {
                  dispatch(resetAlertMessages());
                  dispatch(resetPageSummary());
                  dispatch(resetCampaign());
                  dispatch(resetCampaignPreview());
                  dispatch(setCampaignData({ openMode: OPEN_MODE.CREATE }));
                  dispatch(setSelectedRoute(routerUrl.CAMPAIGN_CREATE_DATA));
                  history.push(routerUrl.CAMPAIGN_CREATE_DATA);
                }}
              >
                Create a new Campaign
              </button>
            </section>
          </div>
          <CalendarCard />
        </ParagraphWithCardWrapper>
      </WelcomeContainer>
      <section>
        <DataTable
          testId="tbl-recent-campaigns"
          loading={campaignListLoading}
          config={recentCampaignsTableConfig}
          rows={campaigns}
          caption="My recent campaigns"
          actionButtonText="View all campaigns"
          noDataPlaceholder="Nothing to show yet.
            Here’s your chance to create something."
          showActionButton
          onCellClick={({ col, id, row }) => {
            if (isCloningEnabled && col?.id === 'clone_campaign') {
              setCampaignToClone(row);
              setCloneModalOpen(true);
              return;
            }

            if (col?.id === 'view_details') {
              dispatch(resetCampaign());
              dispatch(resetCampaignPreview());
              const url = resolveRouteWithId(routerUrl.CAMPAIGN_DETAILS, id);
              history.push(url);
            }
          }}
          onActionButtonClick={() => {
            history.push(routerUrl.CAMPAIGN_SEARCH);
          }}
        />
      </section>
      <CloneCampaignFlowModal
        isOpen={isCloningEnabled && cloneModalOpen}
        campaignName={campaignToClone?.name}
        sourceIncludesFile={cloneSourceIncludesFile}
        onCloneAndRefresh={handleCloneAndRefresh}
        onClose={closeCloneModal}
      />
    </div>
  );
};

export { Home };
